// Portal brand theming (v3 — COMPONENTS.md TOKENS + Recursive Help prototype).
//
// Three brands. Each supplies a surface colour and an ink ramp for that surface.
// DECISIONS.md §12: a brand surface flips INK, not hue — Nautilent's light blue
// (#56A0D3) stays the true brand colour. White ink on it is only ~2.85:1, BELOW
// the 4.5:1 text floor; the design accepts that on the brand ground as a
// deliberate tradeoff (the brand team's approved surface) and runs the white
// near-opaque (0.96/0.90) so it is as legible as possible, rather than
// substituting an unapproved darker blue. Where the 4.5:1 floor actually applies
// — type on WHITE — the darkened same-hue value #1B4F76 (`ink`) is used instead
// (see COMPONENTS.md "Contrast floor"). All three brands render body text in
// Space Grotesk for now; a per-brand UI typeface is an open question (Q3, TODO(Q3)).

export type BrandId = "recursive" | "solidearth" | "nautilent";

export interface BrandSpace {
  id: string;
  name: string;
  kind: string; // "Platform" | "Client space"
  note: string;
  locked: boolean;
}

export interface Brand {
  id: BrandId;
  name: string;
  tag: string; // uppercase eyebrow under the wordmark
  kicker: string; // one-line product descriptor (space switcher)
  blurb: string;
  mark: "gable" | "globe" | "sunburst";
  // brand ink ramp (COMPONENTS.md)
  ink: string;
  deep: string;
  soft: string;
  tint: string;
  line: string;
  // Only set when the brand surface is a light colour that the ink adapts to.
  surface?: string;
  spaces: BrandSpace[];
  pushNote: string; // the two-content-sources explanation in the space switcher
  // Category rows: [icon, name, blurb, articleCount, audience].
  cats: [string, string, string, number, string][];
}

export const BRANDS: Record<BrandId, Brand> = {
  recursive: {
    id: "recursive",
    name: "Recursive",
    tag: "DATA SOVEREIGNTY FOR MLS",
    kicker: "MLS data platform",
    blurb: "The MLS data platform — listing add/edit, distribution, and the governance layer around both.",
    mark: "gable",
    ink: "#006747",
    deep: "#00543a",
    soft: "#f1f6f3",
    tint: "rgba(0,103,71,.09)",
    line: "rgba(0,103,71,.2)",
    pushNote: "Platform docs are written by us and shared by every MLS. Each client space is your own.",
    spaces: [
      { id: "platform", name: "Platform docs", kind: "Platform", note: "Version-tracked, shared by every MLS", locked: false },
      { id: "ccar", name: "Coastal Carolinas MLS", kind: "Client space", note: "12,480 members · your home space", locked: false },
      { id: "trng", name: "Triangle Regional MLS", kind: "Client space", note: "8,140 members", locked: false },
      { id: "gsmls", name: "Great Southern MLS", kind: "Client space", note: "Requires board admin role", locked: true },
    ],
    cats: [
      ["rocket", "Getting started", "Your first week in Recursive — orientation, roles, and the vocabulary.", 14, "MLS admin"],
      ["pencil-ruler", "Listing add/edit", "Entering, editing and validating listings. Field rules and photo handling.", 31, "Agent"],
      ["share-2", "Data distribution", "Feeds, consumers, API clients, and what leaves your system.", 26, "Developer"],
      ["shield-check", "RESO & compliance", "Certification, field mapping, and the audit trail your board will ask for.", 19, "MLS admin"],
      ["users", "Roles & permissions", "Who can see and change what, across offices and associations.", 12, "Broker"],
      ["bar-chart-3", "Reports & analytics", "Usage, market stats, and exporting anything you can see.", 17, "MLS admin"],
    ],
  },
  solidearth: {
    id: "solidearth",
    name: "Solid Earth",
    tag: "SECURE IDENTITY FOR MLS & ASSOCIATIONS",
    kicker: "Member & association sites",
    blurb: "Member-facing search, association websites, and the identity layer that signs everyone in.",
    mark: "globe",
    ink: "#2c384d",
    deep: "#1f2838",
    soft: "#f3f4f7",
    tint: "rgba(44,56,77,.08)",
    line: "rgba(44,56,77,.2)",
    pushNote: "Platform docs are written by us and shared across all association sites. Each client space is your own.",
    spaces: [
      { id: "platform", name: "Platform docs", kind: "Platform", note: "Shared across all association sites", locked: false },
      { id: "ccar", name: "Coastal Carolinas MLS", kind: "Client space", note: "Your home space", locked: false },
      { id: "nwar", name: "Northwest Association", kind: "Client space", note: "4,220 members", locked: false },
    ],
    cats: [
      ["search", "Member search", "Saved searches, map draw, hotsheets, and what members see first.", 22, "Agent"],
      ["key-round", "SSO & identity", "SAML, OIDC, and mapping your IdP groups onto Displet roles.", 18, "Developer"],
      ["layout-template", "Association websites", "Page building, content blocks, and publishing without a deploy.", 24, "MLS admin"],
      ["smartphone", "Displet IDX", "Broker and agent IDX sites, lead routing, and syndication rules.", 20, "Broker"],
      ["mail", "Member communications", "Blasts, drip campaigns, and compliance on outbound mail.", 15, "MLS admin"],
      ["life-buoy", "Support & escalation", "How a member ticket reaches us, and what we need in it.", 9, "Broker"],
    ],
  },
  nautilent: {
    id: "nautilent",
    name: "Nautilent",
    tag: "BROKERAGE OPERATING PLATFORM",
    kicker: "Brokerage back office",
    blurb: "The back office for enterprise brokerages — transactions, commissions, recruiting, and rosters.",
    mark: "sunburst",
    ink: "#1B4F76",
    deep: "#143b59",
    soft: "#f0f7fc",
    tint: "rgba(86,160,211,.14)",
    line: "rgba(27,79,118,.22)",
    surface: "#56A0D3", // the brand blue; the header/sign-in ground
    pushNote: "Platform docs are written by us and shared across brokerages. Each client space is your own.",
    spaces: [
      { id: "platform", name: "Platform docs", kind: "Platform", note: "Shared across brokerages", locked: false },
      { id: "harbor", name: "Harbor & Pine Realty", kind: "Client space", note: "1,860 agents · your home space", locked: false },
      { id: "meridian", name: "Meridian Property Group", kind: "Client space", note: "940 agents", locked: false },
    ],
    cats: [
      ["file-signature", "Transactions", "Opening a file, checklists, documents, and closing out.", 28, "Agent"],
      ["percent", "Commissions & plans", "Splits, caps, anniversary logic, and mid-year plan changes.", 21, "Broker"],
      ["user-plus", "Recruiting & onboarding", "Pipelines, offer letters, and day-one provisioning.", 16, "Broker"],
      ["building-2", "Offices & rosters", "Structure, licensing status, and roster sync with the MLS.", 13, "Broker"],
      ["receipt", "Accounting exports", "Ledger exports, 1099 prep, and reconciling against the bank.", 11, "MLS admin"],
      ["plug", "Integrations", "Webhooks, the public API, and pushing data into your warehouse.", 14, "Developer"],
    ],
  },
};

export const BRAND_ORDER: BrandId[] = ["recursive", "solidearth", "nautilent"];

/**
 * The CSS custom properties for a brand's chrome, exactly as the prototype
 * derives them (Recursive Help.dc.html §renderVals). `surface` is the header /
 * sign-in ground; the `on*` ramp is white overlay, run near-opaque on a light
 * surface (Nautilent) to maximise legibility on the approved brand ground (see
 * the contrast note at the top of this file). Spread onto a wrapper's `style`.
 */
export function brandVars(brand: Brand): Record<string, string> {
  const lightSurface = Boolean(brand.surface);
  return {
    "--bi": brand.ink,
    "--bd": brand.deep,
    "--bs": brand.soft,
    "--bt": brand.tint,
    "--bl": brand.line,
    "--sf": brand.surface ?? brand.ink,
    "--on": "#fff",
    "--on2": lightSurface ? "rgba(255,255,255,.96)" : "rgba(255,255,255,.84)",
    "--on3": lightSurface ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.62)",
    "--onBd": lightSurface ? "rgba(255,255,255,.5)" : "rgba(255,255,255,.26)",
    "--onBg": lightSurface ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.11)",
    "--onBg2": lightSurface ? "rgba(255,255,255,.32)" : "rgba(255,255,255,.2)",
  };
}
